import React, { type ReactElement, useEffect, useMemo } from "react";
import { AccessibilityInfo, Platform, StyleSheet, View, type ViewStyle } from "react-native";
import Svg, { Path } from "react-native-svg";

import { Text } from "../Text";
import { useTheme } from "../ThemeProvider";
import type { StrictPaddingStyle } from "../util/types";

export interface TooltipProps {
  /**
   * The alignment along the specified side.
   */
  alignment?: "center" | "start" | "end";

  /**
   * Text for the tooltip.
   */
  label: string;

  /**
   * Defines the side of the anchor to render against when visible.
   */
  side: "inline-start" | "inline-end" | "block-start" | "block-end";

  /**
   * The visibility state for controlled tooltips.
   */
  visible?: boolean;

  /**
   * Maximum width of the tooltip container.
   */
  maxWidth?: number;

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;

  /**
   * Web: Sets the id attribute on the tooltip container.
   * Use with aria-describedby on the trigger element so screen readers
   * announce tooltip text when focus lands on the trigger.
   */
  nativeID?: string;
}

export function Tooltip({
  label,
  alignment = "center",
  side,
  visible,
  maxWidth = 250,
  sx,
  nativeID,
}: TooltipProps): ReactElement {
  const isWeb = Platform.OS === "web";
  const theme = useTheme();
  const position = sideToPosition(side);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        tooltipContainer: {
          position: "absolute",
          zIndex: 1000,
        },
        tooltip: {
          backgroundColor: theme.colors.backgroundInverse,
          paddingVertical: theme.spacing.s2,
          paddingHorizontal: theme.spacing.s4,
          borderRadius: theme.borderRadius.md,
          alignItems: "center",
          minWidth: "auto",
        },
        tooltipText: {
          color: theme.colors.foregroundInverse,
          textAlign: "center",
          flexShrink: 1,
        },
        tooltipArrow: {
          position: "absolute",
          width: 16,
          height: 9,
        },
        hidden: {
          display: "none",
        },
      }),
    [theme],
  );

  // Announce tooltip for iOS

  useEffect(() => {
    if (isWeb || visible === false) return;

    /* SetTimeout is used to ensure that the tooltip content is announced after the button text change is announced.
            This way the behavior is consistent with the behavior on Android.
        */
    const timer = setTimeout(() => {
      AccessibilityInfo.announceForAccessibility(label);
    }, 1500);

    return () => clearTimeout(timer);
  }, [isWeb, visible, label]);

  const containerStyle: ViewStyle = {
    ...styles.tooltipContainer,
    ...getPositionStyle(position),
    ...getAlignmentStyle(alignment, position, maxWidth),
    ...(visible === false ? styles.hidden : {}),
  };

  const wrapperStyle = getWrapperStyle(alignment, position);
  const arrowPositionStyle = getArrowPositionStyle(position, alignment);

  return (
    <View
      style={containerStyle}
      accessibilityLiveRegion={isWeb ? undefined : "polite"}
      nativeID={nativeID}
      role={isWeb ? "tooltip" : undefined}
    >
      <View style={wrapperStyle}>
        <View style={[styles.tooltip, { maxWidth }, sx]}>
          <Text level={2} sx={styles.tooltipText}>
            {label}
          </Text>
        </View>
        <View
          style={[styles.tooltipArrow, arrowPositionStyle]}
          accessible={false}
          aria-hidden={isWeb ? true : undefined}
        >
          <Svg width="16" height="9" viewBox="0 0 16 9" accessible={false} focusable={false}>
            <Path d={getArrowPath()} fill={theme.colors.backgroundInverse} />
          </Svg>
        </View>
      </View>
    </View>
  );
}

function sideToPosition(
  side: "inline-start" | "inline-end" | "block-start" | "block-end",
): "top" | "bottom" | "left" | "right" {
  switch (side) {
    case "block-start":
      return "top";
    case "block-end":
      return "bottom";
    case "inline-start":
      return "left";
    case "inline-end":
      return "right";
  }
}

function getPositionStyle(position: "top" | "bottom" | "left" | "right"): ViewStyle {
  return positionStyles[position] || {};
}

function getAlignmentStyle(
  alignment: "center" | "start" | "end",
  position: "top" | "bottom" | "left" | "right",
  maxWidth: number,
): ViewStyle {
  if (position === "top" || position === "bottom") {
    switch (alignment) {
      case "start":
        return { left: 0, right: undefined, width: maxWidth };
      case "end":
        return { right: 0, left: undefined, width: maxWidth };
      case "center":
      default:
        return {
          left: "50%",
          transform: [{ translateX: "-50%" }],
          right: undefined,
          width: maxWidth,
        };
    }
  } else {
    switch (alignment) {
      case "start":
        return { top: 0, bottom: undefined };
      case "end":
        return { bottom: 0, top: undefined };
      case "center":
      default:
        return { top: "50%", transform: [{ translateY: "-50%" }] };
    }
  }
}

function getWrapperStyle(
  alignment: "center" | "start" | "end",
  position: "top" | "bottom" | "left" | "right",
): ViewStyle {
  if (position === "top" || position === "bottom") {
    switch (alignment) {
      case "start":
        return tooltipWrapperStyles.start;
      case "end":
        return tooltipWrapperStyles.end;
      case "center":
      default:
        return tooltipWrapperStyles.center;
    }
  } else {
    // For left/right positions, always center vertically
    return tooltipWrapperStyles.center;
  }
}

function getArrowPositionStyle(
  position: "top" | "bottom" | "left" | "right",
  alignment: "center" | "start" | "end",
): ViewStyle {
  let style: ViewStyle = {};

  switch (position) {
    case "top":
      style = { bottom: 0, transform: [{ translateY: 9 }] };
      break;
    case "bottom":
      style = { top: 0, transform: [{ translateY: -9 }, { rotate: "180deg" }] };
      break;
    case "left":
      style = { right: 0, transform: [{ translateX: 9 }, { rotate: "-90deg" }] };
      break;
    case "right":
      style = { left: 0, transform: [{ translateX: -9 }, { rotate: "90deg" }] };
      break;
  }

  if (position === "top" || position === "bottom") {
    switch (alignment) {
      case "start":
        style.left = 16;
        style.right = undefined;
        break;
      case "end":
        style.right = 16;
        style.left = undefined;
        break;
      case "center":
      default:
        style.left = "50%";
        style.marginLeft = -8;
        style.right = undefined;
    }
  } else if (position === "left" || position === "right") {
    switch (alignment) {
      case "start":
        style.top = 16;
        style.bottom = undefined;
        break;
      case "end":
        style.bottom = 16;
        style.top = undefined;
        break;
      case "center":
      default:
        style.top = "50%";
        style.marginTop = -4.5;
        style.bottom = undefined;
    }
  }

  return style;
}

function getArrowPath() {
  return "M0 0 L8 9 L16 0";
}

const tooltipWrapperStyles = StyleSheet.create({
  center: {
    alignItems: "center",
  },
  start: {
    alignItems: "flex-start",
  },
  end: {
    alignItems: "flex-end",
  },
});

const positionStyles = StyleSheet.create({
  top: {
    bottom: "100%",
    marginBottom: 10,
  },
  bottom: {
    top: "100%",
    marginTop: 10,
  },
  left: {
    right: "100%",
    marginRight: 10,
  },
  right: {
    left: "100%",
    marginLeft: 10,
  },
});
